import React, { useMemo, useState } from "react";
import {
    Card, Col, Row, Statistic, Skeleton, Table, Tag, Progress,
    Tabs, Typography, Tooltip, Alert, Badge, Space, Divider,
} from "antd";
import {
    ThunderboltOutlined,
    WarningOutlined,
    FileTextOutlined,
    HistoryOutlined,
    ExperimentOutlined,
    QuestionCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    FireOutlined,
    ClockCircleOutlined,
    RiseOutlined,
} from "@ant-design/icons";
import { useSupplies } from "../context/SuppliesContext";

const { Text, Title } = Typography;

// ─── Утилиты ────────────────────────────────────────────────────────────────

function normKey(name) {
    if (!name) return "";
    return name.trim().toLowerCase()
        .replace(/анодированый/g, "анодированный")
        .replace(/черный/g, "чёрный")
        .replace(/\s+/g, " ");
}

// ─── Теги ────────────────────────────────────────────────────────────────────

const RiskTag = React.memo(({ row }) => {
    if (row.riskStatus === 'critical') return <Tag color="red" style={{ fontWeight: 700 }}>🔴 Критично</Tag>;
    if (row.riskStatus === 'warning') return <Tag color="orange">🟠 Скоро заказывать</Tag>;
    if (row.riskStatus === 'ok') return <Tag color="green">🟢 В норме</Tag>;
    return <Tag color="default">— Нет данных</Tag>;
});

const SourceTag = React.memo(({ row }) => {
    if (row.source === 'historical') {
        return (
            <Tooltip title={`Реальные списания: ${row.movementsCount} операций за 90 дней`}>
                <Tag icon={<HistoryOutlined />} color="blue">История</Tag>
            </Tooltip>
        );
    }
    if (row.source === 'estimated') {
        const tip = row.neededThisMonth > 0
            ? `Оценка по заявкам текущего месяца: ${row.neededThisMonth} ${row.unit}/мес → ${row.dailyUsage} ${row.unit}/день`
            : `Оценка по всем активным заявкам: ${row.activeNeeded} ${row.unit} / 90 дн.`;
        return (
            <Tooltip title={tip}>
                <Tag icon={<ExperimentOutlined />} color="gold">Оценка</Tag>
            </Tooltip>
        );
    }
    return (
        <Tooltip title="Нет истории списаний и нет активных заявок">
            <Tag icon={<QuestionCircleOutlined />} color="default">—</Tag>
        </Tooltip>
    );
});

// ─── Сводные карточки KPI ────────────────────────────────────────────────────

const ForecastKPICards = React.memo(({ data }) => {
    const critical = data.filter(r => r.riskStatus === 'critical').length;
    const warning  = data.filter(r => r.riskStatus === 'warning').length;
    const ok       = data.filter(r => r.riskStatus === 'ok').length;
    const needOrder = data.filter(r => r.toOrder > 0).length;

    const cards = [
        {
            label: "🔴 Срочно заказать",
            value: critical,
            color: "#ff4d4f",
            sub: "Не доживут до поставки",
        },
        {
            label: "🟠 Скоро заказывать",
            value: warning,
            color: "#fa8c16",
            sub: "Приближаются к рубежу",
        },
        {
            label: "🟢 В норме",
            value: ok,
            color: "#52c41a",
            sub: "Запаса достаточно",
        },
        {
            label: "📦 Нужен заказ",
            value: needOrder,
            color: "#1677ff",
            sub: `Всего к заказу: ${data.reduce((s, r) => s + (r.toOrder || 0), 0)} ед.`,
        },
    ];

    return (
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            {cards.map(c => (
                <Col xs={12} sm={6} key={c.label}>
                    <Card
                        size="small"
                        style={{ borderRadius: 10, borderTop: `3px solid ${c.color}`, height: "100%" }}
                    >
                        <Statistic
                            title={<span style={{ fontSize: 12 }}>{c.label}</span>}
                            value={c.value}
                            valueStyle={{ color: c.color, fontSize: 28, fontWeight: 700 }}
                        />
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>{c.sub}</div>
                    </Card>
                </Col>
            ))}
        </Row>
    );
});

// ─── Управленческий вывод — что делать прямо сейчас ─────────────────────────

const ActionSummary = React.memo(({ data }) => {
    const critical = data.filter(r => r.riskStatus === 'critical');
    const warning  = data.filter(r => r.riskStatus === 'warning' && r.toOrder > 0);

    if (critical.length === 0 && warning.length === 0) {
        return (
            <Alert
                message="Все позиции в норме — срочных заказов нет"
                description="Текущих остатков и позиций «в пути» достаточно для покрытия сроков поставок."
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                style={{ marginBottom: 16 }}
            />
        );
    }

    return (
        <div style={{ marginBottom: 16 }}>
            {critical.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    icon={<FireOutlined />}
                    style={{ marginBottom: 8 }}
                    message={
                        <span>
                            <Text type="danger" strong>Срочно заказать: {critical.length} позиций</Text>
                            {" — остатков не хватит до прихода следующей партии"}
                        </span>
                    }
                    description={
                        <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
                            {critical.slice(0, 5).map(r => (
                                <li key={r.key}>
                                    <Text strong>{r.materialName}</Text>
                                    {" — хватит на "}
                                    <Text type="danger" strong>{r.daysLeft ?? "0"} дн.</Text>
                                    {", срок поставки "}
                                    <Text strong>{r.deliveryDays} дн.</Text>
                                    {r.toOrder > 0 && (
                                        <Text type="danger"> → заказать {r.toOrder} {r.unit}</Text>
                                    )}
                                </li>
                            ))}
                            {critical.length > 5 && (
                                <li style={{ color: "#8c8c8c" }}>...и ещё {critical.length - 5} позиций</li>
                            )}
                        </ul>
                    }
                />
            )}
            {warning.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message={
                        <span>
                            <Text style={{ color: "#d46b08" }} strong>Заказать в ближайшее время: {warning.length} позиций</Text>
                        </span>
                    }
                    description={
                        <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
                            {warning.slice(0, 4).map(r => (
                                <li key={r.key}>
                                    <Text strong>{r.materialName}</Text>
                                    {" — хватит на "}
                                    <Text style={{ color: "#d46b08" }} strong>{r.daysLeft ?? "?"} дн.</Text>
                                    {", срок поставки "}
                                    <Text strong>{r.deliveryDays} дн.</Text>
                                    {r.toOrder > 0 && (
                                        <Text style={{ color: "#d46b08" }}> → заказать {r.toOrder} {r.unit}</Text>
                                    )}
                                </li>
                            ))}
                            {warning.length > 4 && (
                                <li style={{ color: "#8c8c8c" }}>...и ещё {warning.length - 4} позиций</li>
                            )}
                        </ul>
                    }
                />
            )}
        </div>
    );
});

// ─── Основная таблица прогноза ────────────────────────────────────────────────

const SupplyForecastTable = React.memo(({ data, loading }) => {
    if (loading) return <Skeleton active paragraph={{ rows: 10 }} />;

    const safeData = Array.isArray(data) ? data : [];

    if (safeData.length === 0) {
        return (
            <Alert
                message="Нет данных для прогноза"
                description="Добавьте позиции на склад или дождитесь появления активных заявок. Данные по расходу берутся из операций списания (OUTGOING) за последние 90 дней."
                type="info"
                showIcon
            />
        );
    }

    const columns = [
        {
            title: "№",
            key: "num",
            width: 40,
            align: "center",
            render: (_, __, i) => <Text type="secondary" style={{ fontSize: 11 }}>{i + 1}</Text>,
        },
        {
            title: (
                <Tooltip title="Источник данных: реальные списания или оценка">
                    <span>Данные</span>
                </Tooltip>
            ),
            key: "source",
            width: 90,
            align: "center",
            render: (_, r) => <SourceTag row={r} />,
            filters: [
                { text: '📊 История', value: 'historical' },
                { text: '📋 Оценка', value: 'estimated' },
                { text: '— Нет данных', value: 'no_data' },
            ],
            onFilter: (value, r) => r.source === value,
        },
        {
            title: "Материал",
            dataIndex: "materialName",
            key: "materialName",
            width: 200,
            ellipsis: true,
            render: (text, r) => (
                <span style={{
                    fontWeight: r.riskStatus === 'critical' ? 700 :
                                r.riskStatus === 'warning' ? 600 : 400
                }}>
                    {text}
                </span>
            ),
        },
        {
            title: (
                <Tooltip title="Текущий остаток на складе">
                    <span>На складе</span>
                </Tooltip>
            ),
            dataIndex: "inStock",
            key: "inStock",
            width: 85,
            align: "center",
            sorter: (a, b) => a.inStock - b.inStock,
            render: (v, r) => (
                <span style={{
                    fontWeight: 600,
                    color: v === 0 ? "#ff4d4f"
                         : v < r.neededForDelivery ? "#faad14"
                         : "#389e0d"
                }}>
                    {v} <Text type="secondary" style={{ fontSize: 10 }}>{r.unit}</Text>
                </span>
            ),
        },
        {
            title: (
                <Tooltip title="Фактические списания OUTGOING за последние 90 дней">
                    <span>Расход 90 дн.</span>
                </Tooltip>
            ),
            dataIndex: "consumption90",
            key: "consumption90",
            width: 100,
            align: "center",
            sorter: (a, b) => a.consumption90 - b.consumption90,
            render: (v, r) =>
                r.source === 'no_data'
                    ? <Text type="secondary">—</Text>
                    : <span>{v} <Text type="secondary" style={{ fontSize: 10 }}>{r.unit}</Text></span>,
        },
        {
            title: (
                <Tooltip title="Расход за 90 дней ÷ 90 = сколько уходит в день">
                    <span>в день</span>
                </Tooltip>
            ),
            dataIndex: "dailyUsage",
            key: "dailyUsage",
            width: 80,
            align: "center",
            sorter: (a, b) => a.dailyUsage - b.dailyUsage,
            render: (v, r) =>
                r.source === 'no_data'
                    ? <Text type="secondary">—</Text>
                    : <Text type="secondary" style={{ fontSize: 12 }}>{v} {r.unit}/д</Text>,
        },
        {
            title: (
                <Tooltip title="Срок поставки из карточки склада">
                    <span>Поставка</span>
                </Tooltip>
            ),
            dataIndex: "deliveryDays",
            key: "deliveryDays",
            width: 80,
            align: "center",
            sorter: (a, b) => a.deliveryDays - b.deliveryDays,
            render: v => (
                <Tag color={v >= 30 ? "red" : v >= 14 ? "orange" : "default"}>
                    {v} дн.
                </Tag>
            ),
        },
        {
            title: (
                <Tooltip title="На сколько дней хватит складского остатка: На складе ÷ Расход/день">
                    <span>Дней хватит</span>
                </Tooltip>
            ),
            dataIndex: "daysLeft",
            key: "daysLeft",
            width: 100,
            align: "center",
            defaultSortOrder: "ascend",
            sorter: (a, b) => (a.daysLeft ?? 9999) - (b.daysLeft ?? 9999),
            render: (v, r) => {
                if (v === null || v === undefined) return <Text type="secondary">—</Text>;
                const color = v < r.deliveryDays ? 'red'
                            : v < r.deliveryDays * 1.5 ? 'orange'
                            : 'green';
                return (
                    <Tooltip title={
                        r.daysLeftWithTransit !== null && r.daysLeftWithTransit !== v
                            ? `С учётом «в пути»: ${r.daysLeftWithTransit} дн.`
                            : undefined
                    }>
                        <Tag color={color} style={{ fontWeight: 600, fontSize: 13 }}>
                            {v} дн.
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: (
                <Tooltip title="Расход/день × Срок поставки — сколько уйдёт пока едет партия">
                    <span>Нужно на поставку</span>
                </Tooltip>
            ),
            dataIndex: "neededForDelivery",
            key: "neededForDelivery",
            width: 130,
            align: "center",
            render: (v, r) =>
                r.source === 'no_data'
                    ? <Text type="secondary">—</Text>
                    : <span style={{ fontWeight: 500 }}>{v} {r.unit}</span>,
        },
        {
            title: (
                <Tooltip title="Закуплено, но ещё не доставлено на склад">
                    <span>В пути</span>
                </Tooltip>
            ),
            dataIndex: "inTransit",
            key: "inTransit",
            width: 72,
            align: "center",
            render: (v, r) =>
                v > 0
                    ? <Tag color="purple">{v} {r.unit}</Tag>
                    : <Text type="secondary">0</Text>,
        },
        {
            title: (
                <Tooltip title="max(0, Нужно на поставку − На складе − В пути)">
                    <span>К заказу</span>
                </Tooltip>
            ),
            dataIndex: "toOrder",
            key: "toOrder",
            width: 90,
            align: "center",
            sorter: (a, b) => b.toOrder - a.toOrder,
            render: (v, r) => {
                if (r.source === 'no_data') return <Text type="secondary">—</Text>;
                return v > 0
                    ? <Text type="danger" strong style={{ fontSize: 14 }}>{v} {r.unit}</Text>
                    : <Text style={{ color: "#52c41a" }}>✓</Text>;
            },
        },
        {
            title: "Статус",
            key: "riskStatus",
            width: 155,
            align: "center",
            filters: [
                { text: '🔴 Критично', value: 'critical' },
                { text: '🟠 Скоро заказывать', value: 'warning' },
                { text: '🟢 В норме', value: 'ok' },
                { text: '— Нет данных', value: 'no_data' },
            ],
            onFilter: (value, r) => r.riskStatus === value,
            render: (_, r) => <RiskTag row={r} />,
        },
    ];

    return (
        <Table
            size="small"
            bordered
            columns={columns}
            dataSource={safeData}
            rowKey="key"
            rowClassName={r =>
                r.riskStatus === 'critical' ? 'row-danger' :
                r.riskStatus === 'warning' ? 'row-warning' : ''
            }
            pagination={{ pageSize: 30, showSizeChanger: true, pageSizeOptions: [15, 30, 50, 100] }}
            scroll={{ x: 1260 }}
        />
    );
});

// ─── Прогноз снабжения (полный блок) ─────────────────────────────────────────

const SupplyForecastBlock = React.memo(({ data, loading }) => {
    const safeData = Array.isArray(data) ? data : [];

    if (loading) return <Skeleton active paragraph={{ rows: 10 }} />;

    return (
        <div>
            <ForecastKPICards data={safeData} />
            <ActionSummary data={safeData} />
            <SupplyForecastTable data={safeData} loading={false} />
        </div>
    );
});

// ─── Блок: Потребление — топ быстрых расходников ────────────────────────────

const ConsumptionBlock = React.memo(({ supplyForecastData }) => {
    const consumptionData = useMemo(() => {
        if (!Array.isArray(supplyForecastData) || supplyForecastData.length === 0) return [];
        return supplyForecastData
            .filter(r => r.source !== 'no_data' && r.dailyUsage > 0)
            .sort((a, b) => b.dailyUsage - a.dailyUsage);
    }, [supplyForecastData]);

    if (consumptionData.length === 0) {
        return (
            <Alert
                message="Нет данных по расходу"
                description="Список появится после накопления истории списаний со склада (операции OUTGOING). Минимум — 1 операция за 90 дней."
                type="info"
                showIcon
            />
        );
    }

    const columns = [
        { title: "№", key: "num", width: 40, align: "center", render: (_, __, i) => <Text type="secondary">{i + 1}</Text> },
        {
            title: "Материал",
            dataIndex: "materialName",
            key: "materialName",
            width: 220,
            render: (text, r) => (
                <span style={{ fontWeight: r.riskStatus === 'critical' ? 700 : 400 }}>{text}</span>
            ),
        },
        {
            title: "Данные",
            key: "source",
            width: 90,
            align: "center",
            render: (_, r) => <SourceTag row={r} />,
        },
        {
            title: "Расход / день",
            dataIndex: "dailyUsage",
            key: "dailyUsage",
            width: 115,
            align: "center",
            render: (v, r) => <Text strong style={{ color: "#1677ff" }}>{v} {r.unit}/дн.</Text>,
        },
        {
            title: "Расход 90 дн.",
            dataIndex: "consumption90",
            key: "consumption90",
            width: 110,
            align: "center",
            render: (v, r) => <span>{v} {r.unit}</span>,
        },
        {
            title: "На складе",
            dataIndex: "inStock",
            key: "inStock",
            width: 85,
            align: "center",
            render: (v, r) => (
                <span style={{
                    fontWeight: 600,
                    color: v === 0 ? "#ff4d4f" : v < r.neededForDelivery ? "#faad14" : "#389e0d"
                }}>
                    {v} {r.unit}
                </span>
            ),
        },
        {
            title: "Дней хватит",
            dataIndex: "daysLeft",
            key: "daysLeft",
            width: 100,
            align: "center",
            render: (v, r) => {
                if (v === null || v === undefined) return <Text type="secondary">—</Text>;
                const color = v < r.deliveryDays ? 'red' : v < r.deliveryDays * 1.5 ? 'orange' : 'green';
                return <Tag color={color}>{v} дн.</Tag>;
            },
        },
        {
            title: "К заказу",
            dataIndex: "toOrder",
            key: "toOrder",
            width: 85,
            align: "center",
            render: (v, r) => v > 0
                ? <Text type="danger" strong>{v} {r.unit}</Text>
                : <Text style={{ color: "#52c41a" }}>✓ 0</Text>,
        },
        {
            title: "Статус",
            key: "riskStatus",
            width: 150,
            align: "center",
            render: (_, r) => <RiskTag row={r} />,
        },
    ];

    return (
        <div>
            <Alert
                message="Топ потребляемых материалов"
                description="Отсортировано по среднесуточному потреблению — от самых быстрых расходников к медленным."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <Table
                size="small"
                bordered
                columns={columns}
                dataSource={consumptionData}
                rowKey="key"
                rowClassName={r =>
                    r.riskStatus === 'critical' ? 'row-danger' :
                    r.riskStatus === 'warning' ? 'row-warning' : ''
                }
                pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ x: 1050 }}
            />
        </div>
    );
});

// ─── Блок: Договора ──────────────────────────────────────────────────────────

const ContractsSummary = React.memo(({ rawOrders, planData, loading }) => {
    const { summary, contracts } = useMemo(() => {
        if (!rawOrders || rawOrders.length === 0) {
            return { summary: { total: 0, totalDoors: 0, covered: 0, notCovered: 0 }, contracts: [] };
        }

        const materialMap = {};
        (planData || []).forEach(p => { materialMap[normKey(p.name)] = p; });

        const list = rawOrders.map(order => {
            const accessories = order.accessories || [];
            const doorCount = order.doorCount || 0;
            const materials = [];

            accessories.forEach(acc => {
                const name = acc.name || "";
                const count = Math.round(acc.count || 0);
                if (count <= 0) return;
                const key = normKey(name);
                const matInfo = materialMap[key];
                materials.push({
                    name, count,
                    unit: acc.unit || "шт.",
                    inStock: matInfo?.inStock || 0,
                    needed: matInfo?.needed || 0,
                });
            });

            const totalNeeded = materials.reduce((s, m) => s + m.count, 0);
            const totalInStock = materials.reduce((s, m) => s + Math.min(m.count, m.inStock), 0);
            const isCovered = totalNeeded > 0 && totalInStock >= totalNeeded;
            const coverPct = totalNeeded > 0 ? Math.min(100, Math.round((totalInStock / totalNeeded) * 100)) : 0;

            return {
                key: order._id,
                contractNum: order.contractNum || order._id || "б/н",
                clientName: order.clientName || "—",
                deadline: order.deadline || "—",
                doorCount,
                materialsCount: materials.length,
                totalNeeded,
                isCovered,
                coverPct,
                materials,
                priority: order.status || "—",
                purchaseStatus: order.accessoriesPurchaseStatus || "NOT_PURCHASED",
            };
        });

        return {
            summary: {
                total: list.length,
                totalDoors: list.reduce((s, c) => s + c.doorCount, 0),
                covered: list.filter(c => c.isCovered).length,
                notCovered: list.filter(c => !c.isCovered).length,
            },
            contracts: list,
        };
    }, [rawOrders, planData]);

    if (loading) return <Skeleton active />;

    const statusLabel = s => {
        if (s === "PURCHASED") return <Tag color="green">Закуплено</Tag>;
        if (s === "SHIPPED") return <Tag color="blue">Отгружено</Tag>;
        return <Tag color="red">Незакуплено</Tag>;
    };

    const expandedRowRender = record => {
        const cols = [
            { title: "Материал", dataIndex: "name", key: "name" },
            { title: "Кол-во", dataIndex: "count", key: "count", align: "center", width: 80 },
            { title: "Ед.", dataIndex: "unit", key: "unit", align: "center", width: 60 },
            {
                title: "На складе",
                dataIndex: "inStock",
                key: "inStock",
                align: "center",
                width: 100,
                render: (v, r) => (
                    <span style={{ color: v >= r.count ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>{v}</span>
                ),
            },
        ];
        return (
            <Table
                size="small"
                columns={cols}
                dataSource={record.materials.map((m, i) => ({ ...m, key: i }))}
                pagination={false}
                style={{ margin: "8px 0" }}
            />
        );
    };

    const columns = [
        { title: "Договор", dataIndex: "contractNum", key: "contractNum", width: 130, render: v => <Text strong>{v}</Text> },
        { title: "Клиент", dataIndex: "clientName", key: "clientName", width: 160 },
        { title: "Срок", dataIndex: "deadline", key: "deadline", width: 100, align: "center" },
        {
            title: "Дверей",
            dataIndex: "doorCount",
            key: "doorCount",
            width: 70,
            align: "center",
            render: v => <Badge count={v} showZero color="#1677ff" />,
        },
        { title: "Материалов", dataIndex: "materialsCount", key: "materialsCount", width: 100, align: "center" },
        {
            title: "Закупка",
            dataIndex: "purchaseStatus",
            key: "purchaseStatus",
            width: 120,
            align: "center",
            render: v => statusLabel(v),
        },
        {
            title: "Покрытие материалами",
            key: "cover",
            width: 180,
            align: "center",
            render: (_, r) => (
                <Progress
                    percent={r.coverPct}
                    size="small"
                    strokeColor={r.coverPct < 50 ? "#ff4d4f" : r.coverPct < 100 ? "#faad14" : "#52c41a"}
                    format={p => `${p}%`}
                />
            ),
        },
        {
            title: "Закрыт",
            key: "isCovered",
            width: 75,
            align: "center",
            render: (_, r) => r.isCovered ? <Tag color="green">✅</Tag> : <Tag color="red">❌</Tag>,
        },
    ];

    return (
        <div>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                {[
                    { label: "Договоров", value: summary.total, color: "#1677ff" },
                    { label: "Дверей", value: summary.totalDoors, color: "#722ed1" },
                    { label: "✅ Закрыты", value: summary.covered, color: "#52c41a" },
                    { label: "❌ Не закрыты", value: summary.notCovered, color: "#ff4d4f" },
                ].map(s => (
                    <Col xs={12} sm={6} key={s.label}>
                        <Card size="small" style={{ borderRadius: 8, borderTop: `3px solid ${s.color}` }}>
                            <Statistic title={s.label} value={s.value} valueStyle={{ color: s.color, fontSize: 22 }} />
                        </Card>
                    </Col>
                ))}
            </Row>
            <Table
                size="small"
                bordered
                columns={columns}
                dataSource={contracts}
                expandable={{ expandedRowRender }}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                scroll={{ x: 950 }}
                rowClassName={r => r.isCovered ? "" : r.coverPct < 50 ? "row-danger" : "row-warning"}
            />
        </div>
    );
});

// ─── Главный компонент ───────────────────────────────────────────────────────

const Statistics = () => {
    const { rawOrders = [], planData = [], supplyForecastData = [], loading } = useSupplies();
    const [activeTab, setActiveTab] = useState("forecast");

    const criticalCount = useMemo(() => {
        if (!Array.isArray(supplyForecastData)) return 0;
        return supplyForecastData.filter(r => r?.riskStatus === 'critical').length;
    }, [supplyForecastData]);

    const renderContent = () => {
        switch (activeTab) {
            case "forecast":
                return (
                    <div style={{ paddingTop: 16 }}>
                        <SupplyForecastBlock data={supplyForecastData} loading={loading} />
                    </div>
                );
            case "consumption":
                return (
                    <div style={{ paddingTop: 16 }}>
                        <ConsumptionBlock supplyForecastData={supplyForecastData} />
                    </div>
                );
            case "contracts":
                return (
                    <div style={{ paddingTop: 16 }}>
                        <ContractsSummary rawOrders={rawOrders} planData={planData} loading={loading} />
                    </div>
                );
            default:
                return null;
        }
    };

    const tabItems = useMemo(() => [
        {
            key: "forecast",
            label: (
                <span>
                    <ThunderboltOutlined />
                    {" "}Прогноз снабжения
                    {criticalCount > 0 && (
                        <Badge count={criticalCount} size="small" style={{ marginLeft: 6 }} />
                    )}
                </span>
            ),
        },
        {
            key: "consumption",
            label: <span><RiseOutlined /> Потребление</span>,
        },
        {
            key: "contracts",
            label: <span><FileTextOutlined /> Договора</span>,
        },
    ], [criticalCount]);

    return (
        <div>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="middle"
                style={{ marginTop: 8 }}
            />
            {renderContent()}
        </div>
    );
};

export default Statistics;